import { useT } from '@planetra/i18n';
import { Badge, Tooltip } from '@planetra/ui';
import { RefreshCw, Wifi, WifiOff } from 'lucide-react';
import { useConnectionState } from '../realtime';

/**
 * Whether live updates are arriving (P0-WEB-05).
 *
 * Small on purpose. Realtime is an accelerator, never the source of truth —
 * every number on the page came over HTTP first and is still correct when the
 * socket is down. So this says what is or is not live, and never suggests the
 * page is broken.
 *
 * `disconnected` is the one that has to be visible: a progress bar that stopped
 * moving is indistinguishable from a job that stopped progressing, and only
 * this tells them apart.
 */
export function ConnectionIndicator() {
  const { t } = useT();
  const state = useConnectionState();

  // NFR-11 — colour never carries meaning alone, so each state has its own icon
  // as well as its own tone. @planetra/ui enforces it: a non-neutral Badge
  // without an icon is a type error.
  const { tone, Icon, label, hint } = PRESENTATION[state];

  return (
    <Tooltip content={t(hint)}>
      <Badge tone={tone} icon={Icon}>
        {t(label)}
      </Badge>
    </Tooltip>
  );
}

const PRESENTATION = {
  connected: {
    tone: 'success',
    Icon: Wifi,
    label: 'common:realtime.live',
    hint: 'common:realtime.liveHint',
  },
  connecting: {
    tone: 'info',
    Icon: RefreshCw,
    label: 'common:realtime.connecting',
    hint: 'common:realtime.connectingHint',
  },
  disconnected: {
    tone: 'warning',
    Icon: WifiOff,
    label: 'common:realtime.offline',
    hint: 'common:realtime.offlineHint',
  },
} as const;
